import { client } from './amplifyClient';

/**
 * User Preferences Service
 * CRUD operations for user preferences with comprehensive error handling
 */

export interface UserPreferencesInput {
  temperatureUnit?: 'CELSIUS' | 'FAHRENHEIT';
  windSpeedUnit?: 'MPH' | 'KMH' | 'MS' | 'KNOTS';
  timeFormat?: 'TWELVE_HOUR' | 'TWENTY_FOUR_HOUR';
  theme?: 'LIGHT' | 'DARK' | 'AUTO';
  defaultMapView?: 'SATELLITE' | 'TERRAIN' | 'ROADMAP' | 'HYBRID';
  showWeatherDetails?: boolean;
  showAtmosphericData?: boolean;
  minimumQualityAlert?: 'VIVID' | 'GREAT' | 'GOOD' | 'FAIR' | 'POOR';
  defaultForecastDays?: number;
  showGoldenHour?: boolean;
  showBlueHour?: boolean;
  shareLocation?: boolean;
  allowAnalytics?: boolean;
}

export interface PreferencesError {
  message: string;
  code?: string;
  originalError?: unknown;
}

export class PreferencesServiceError extends Error {
  constructor(
    message: string,
    public code?: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'PreferencesServiceError';
  }
}

/**
 * Get user preferences by userId
 * Returns null if preferences don't exist (user hasn't set them yet)
 */
export async function getUserPreferences(userId: string) {
  try {
    const { data, errors } = await client.models.UserPreferences.list({
      filter: { userId: { eq: userId } },
    });

    if (errors && errors.length > 0) {
      throw new PreferencesServiceError(
        'Failed to fetch user preferences',
        'FETCH_ERROR',
        errors
      );
    }

    // Return first match or null
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    if (error instanceof PreferencesServiceError) {
      throw error;
    }
    throw new PreferencesServiceError(
      'Unexpected error fetching preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Create user preferences with defaults
 * Should only be called once per user during onboarding
 */
export async function createUserPreferences(
  userId: string,
  preferences: Partial<UserPreferencesInput> = {}
) {
  try {
    // Check if preferences already exist
    const existing = await getUserPreferences(userId);
    if (existing) {
      throw new PreferencesServiceError(
        'User preferences already exist',
        'ALREADY_EXISTS'
      );
    }

    const { data, errors } = await client.models.UserPreferences.create({
      userId,
      temperatureUnit: preferences.temperatureUnit ?? 'FAHRENHEIT',
      windSpeedUnit: preferences.windSpeedUnit ?? 'MPH',
      timeFormat: preferences.timeFormat ?? 'TWELVE_HOUR',
      theme: preferences.theme ?? 'AUTO',
      defaultMapView: preferences.defaultMapView ?? 'SATELLITE',
      showWeatherDetails: preferences.showWeatherDetails ?? true,
      showAtmosphericData: preferences.showAtmosphericData ?? false,
      minimumQualityAlert: preferences.minimumQualityAlert ?? 'GOOD',
      defaultForecastDays: preferences.defaultForecastDays ?? 7,
      showGoldenHour: preferences.showGoldenHour ?? true,
      showBlueHour: preferences.showBlueHour ?? true,
      shareLocation: preferences.shareLocation ?? false,
      allowAnalytics: preferences.allowAnalytics ?? true,
    });

    if (errors && errors.length > 0) {
      throw new PreferencesServiceError(
        'Failed to create user preferences',
        'CREATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new PreferencesServiceError(
        'No data returned after creating preferences',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof PreferencesServiceError) {
      throw error;
    }
    throw new PreferencesServiceError(
      'Unexpected error creating preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Update existing user preferences
 * Performs partial updates - only provided fields are updated
 */
export async function updateUserPreferences(
  userId: string,
  updates: Partial<UserPreferencesInput>
) {
  try {
    // Get existing preferences to get the ID
    const existing = await getUserPreferences(userId);

    if (!existing) {
      throw new PreferencesServiceError(
        'User preferences not found. Create preferences first.',
        'NOT_FOUND'
      );
    }

    const { data, errors } = await client.models.UserPreferences.update({
      id: existing.id,
      ...updates,
    });

    if (errors && errors.length > 0) {
      throw new PreferencesServiceError(
        'Failed to update user preferences',
        'UPDATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new PreferencesServiceError(
        'No data returned after updating preferences',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof PreferencesServiceError) {
      throw error;
    }
    throw new PreferencesServiceError(
      'Unexpected error updating preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Delete user preferences
 * Typically used when deleting a user account
 */
export async function deleteUserPreferences(userId: string) {
  try {
    const existing = await getUserPreferences(userId);

    if (!existing) {
      throw new PreferencesServiceError(
        'User preferences not found',
        'NOT_FOUND'
      );
    }

    const { data, errors } = await client.models.UserPreferences.delete({
      id: existing.id,
    });

    if (errors && errors.length > 0) {
      throw new PreferencesServiceError(
        'Failed to delete user preferences',
        'DELETE_ERROR',
        errors
      );
    }

    return data;
  } catch (error) {
    if (error instanceof PreferencesServiceError) {
      throw error;
    }
    throw new PreferencesServiceError(
      'Unexpected error deleting preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}
